import { useTranslation } from 'react-i18next'
import { parse, differenceInYears } from 'date-fns'
import Avatar from 'react-avatar'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import BtnTooltip from '../../../../../UI/BtnTooltip'
import ParentalConfirmation from './ParentalConfirmation'
import { ContactLines } from './personIcons'
import eventEmitter, { types } from '../../../../../shared/eventEmitter'
import { DOCUMENT_TYPES, TLanguages } from '../../../../../shared/const'

import styles from '../style.module.scss'

const VIEWS = { GRID: 'grid', LIST: 'list' }

const CheckIcon = () => (
	<svg
		className={styles.reviewBtnIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={3}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M20 6 9 17l-5-5" />
	</svg>
)

const CrossIcon = () => (
	<svg
		className={styles.reviewBtnIcon}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={3}
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M18 6 6 18" />
		<path d="m6 6 12 12" />
	</svg>
)

const openProfile = item => eventEmitter.emit(types.openStudentProfile, item, () => {})

/**
 * Wie eine hochgeladene Unterlage aussieht.
 *
 * Nur **eine** Lage blinkt: die noch zu pruefende. Geprueft und in Ordnung ist
 * ruhig gruen, abgelehnt ruhig rot — beides ist ein Ergebnis und braucht keine
 * Aufmerksamkeit mehr (Festlegung Peters, 08.08.2026). Vorher stand nach der
 * Pruefung gar keine Farbe an der Datei; das Ergebnis las man nur am Umschalter
 * darunter.
 */
const documentState = (styles, { pending, approved, rejected }) => {
	if (pending) return styles.docPending
	if (rejected) return styles.docRejected
	if (approved) return styles.docApproved
	return null
}

/**
 * Klick auf die Kachel oeffnet das Profil — aber nur, wenn er nicht einem
 * Bedienelement darin galt. Ohne diese Pruefung wuerde jedes „OK" beim
 * Elternnachweis nebenbei das Profilfenster aufziehen.
 */
const cardActivation = item => ({
	role: 'button',
	tabIndex: 0,
	onClick: event => {
		if (event.target.closest('button, a, input, label')) return
		openProfile(item)
	},
	onKeyDown: event => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		if (event.target !== event.currentTarget) return
		event.preventDefault()
		openProfile(item)
	}
})

/**
 * Ein Umschalter fuer eine hochgeladene Unterlage: links ablehnen, rechts
 * bestaetigen. Bis 31.10.2026 benutzte ihn auch der Kursnachweis.
 */
function ReviewSwitch({ caption, pending, locked, rejected, approved, onReject, onApprove }) {
	const { t } = useTranslation()

	return (
		<div className={styles.reviewControl}>
			<span className={styles.reviewCaption}>
				{caption}
				{pending && <span className={styles.reviewPendingHint}> · {t('zu prüfen')}</span>}
			</span>
			<div className={clsx(styles.parentalSwitch, locked && styles.parentalSwitchDisabled)} role="group">
				<button
					type="button"
					disabled={locked}
					className={clsx(
						styles.parentalSwitchHalf,
						styles.parentalSwitchReject,
						rejected && styles.parentalSwitchRejectActive
					)}
					onClick={() => !locked && onReject()}
				>
					<CrossIcon />
					{t('Parental rejected')}
				</button>
				<button
					type="button"
					disabled={locked}
					className={clsx(styles.parentalSwitchHalf, approved && styles.parentalSwitchOkActive)}
					onClick={() => !locked && !approved && onApprove()}
				>
					<CheckIcon />
					{t('OK')}
				</button>
			</div>
		</div>
	)
}

/**
 * Derselbe Befund, nur zum Lesen (Sicht der Lehrkraft): Geprueft hat die
 * Verwaltung, hochgeladen und beanstandet wird an anderer Stelle
 * (Schueler:innenliste der Lehrkraft, Anforderung #94). Ein Umschalter, der
 * nichts umschaltet, waere hier schlimmer als gar keiner.
 */
function ReviewState({ caption, pending, rejected, approved }) {
	const { t } = useTranslation()

	const [label, tone] = rejected
		? [t('Parental rejected'), styles.reviewStateRejected]
		: approved
		? [t('Document reviewed'), styles.reviewStateApproved]
		: pending
		? [t('zu prüfen'), styles.reviewStatePending]
		: [t('Not reviewed yet'), null]

	return (
		<div className={styles.reviewControl}>
			<span className={styles.reviewCaption}>{caption}</span>
			<span className={clsx(styles.reviewState, tone)}>{label}</span>
		</div>
	)
}

ReviewState.propTypes = {
	caption: PropTypes.node.isRequired,
	pending: PropTypes.bool,
	rejected: PropTypes.bool,
	approved: PropTypes.bool
}

ReviewSwitch.propTypes = {
	caption: PropTypes.node.isRequired,
	locked: PropTypes.bool,
	pending: PropTypes.bool,
	rejected: PropTypes.bool,
	approved: PropTypes.bool,
	onReject: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired
}

/**
 * Reiter „Mitarbeiter:innen": die Schueler:innen des Unternehmens und die noch
 * offenen Einladungen. Wahlweise als Kacheln oder als Liste.
 *
 * Schule und Lehrkraefte stehen bei den Basisinformationen, die
 * Wirtschaftsexpert:innen in ihrem eigenen Reiter — hier geht es nur um das
 * Team selbst, samt seiner Unterlagen.
 */
export default function PeopleTab(props) {
	const {
		companyInfo,
		readOnly,
		withoutDocuments,
		employees,
		invitationUser,
		avatarUrls,
		workAreas,
		view,
		onChangeView,
		onRejectParental,
		onApproveParental
	} = props
	const { t, i18n } = useTranslation()

	const isLocked = !!companyInfo?.isActive
	const isList = view === VIEWS.LIST

	const workAreaLabel = wa =>
		(i18n.language === TLanguages.DE
			? workAreas?.find(v => v.value_EN === wa)?.value_DE
			: workAreas?.find(v => v.value_EN === wa)?.value_EN) || wa

	return (
		<section className={styles.block}>
			<div className={styles.blockHead}>
				<h3 className={styles.blockTitle}>
					{t('employees')} ({employees?.length || 0})
				</h3>
				<div className={styles.viewSwitch} role="group" aria-label={t('View')}>
					<button
						type="button"
						className={clsx(styles.viewSwitchBtn, !isList && styles.viewSwitchBtnActive)}
						onClick={() => onChangeView(VIEWS.GRID)}
					>
						{t('Tiles')}
					</button>
					<button
						type="button"
						className={clsx(styles.viewSwitchBtn, isList && styles.viewSwitchBtnActive)}
						onClick={() => onChangeView(VIEWS.LIST)}
					>
						{t('List')}
					</button>
				</div>
			</div>

			<div className={clsx(styles.peopleGrid, isList && styles.peopleList)}>
				{employees?.map((item, index) => {
					const age = item.dateOfBirth
						? differenceInYears(new Date(), parse(item.dateOfBirth, 'yyyy-MM-dd', new Date()))
						: ''
					const functions = [item.primaryWorkArea, item.secondaryWorkArea, item.tertiaryWorkArea].filter(
						Boolean
					)
					const parentalDoc = item.Documents.find(({ type }) => type === DOCUMENT_TYPES.PARENTAL_CONFIRMATION)
					const parentalPending =
						!!parentalDoc && !item.reviewedParentalConfirmation && !item.rejectedParentalConfirmation

					return (
						<div
							key={item.id}
							className={clsx(styles.emploeeContainer, styles.clickableCard, isList && styles.personRow)}
							title={t('Edit Userprofile')}
							{...cardActivation(item)}
						>
							{/* Name und Erreichbarkeit gehoeren zusammen: in der Zeilenansicht
							    steht die Erreichbarkeit deshalb unter dem Namen, nicht in einer
							    eigenen Spalte weit rechts davon. */}
							<div className={styles.personIdentity}>
								<span className={styles.employeeProfile}>
									<Avatar
										className={styles.userAvatar}
										round
										size={32}
										color={
											item.reviewedParentalConfirmation
												? '#2e9b5b'
												: item.rejectedParentalConfirmation
												? '#d14b43'
												: '#e0a73a'
										}
										name={`${item.firstName} ${item.lastName}`}
										src={avatarUrls[item.avatarHashName] || ''}
									/>
									<p className={styles.name}>
										{item.firstName} {item.lastName}{' '}
										{!!age && (
											<span>
												({age}&nbsp;{t('years')})
											</span>
										)}
									</p>
									<BtnTooltip
										items={[
											{
												label: t('Edit Userprofile'),
												onClick: () =>
													eventEmitter.emit(types.openStudentProfile, item, () => {})
											}
										]}
									/>
								</span>
								<ContactLines email={item.email} phone={item.phone} />
							</div>
							<div className={styles.employeeFunctions}>
								<svg
									className={styles.fieldIcon}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<rect x="2" y="7" width="20" height="14" rx="2" />
									<path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
								</svg>
								{functions.length ? (
									functions.map((wa, i) => {
										const isCeo = /ceo/i.test(wa)
										return (
											<span
												key={i}
												className={clsx(styles.functionChip, isCeo && styles.functionChipCeo)}
											>
												{isCeo && (
													<svg
														className={styles.functionStar}
														viewBox="0 0 24 24"
														fill="currentColor"
													>
														<path d="M12 2l2.9 6.2 6.6.7-4.9 4.5 1.4 6.6L12 17.3 5.9 20.5l1.4-6.6L2.5 8.9l6.6-.7z" />
													</svg>
												)}
												{workAreaLabel(wa)}
											</span>
										)
									})
								) : (
									<span className={styles.noFunction}>{t('No main work area yet')}</span>
								)}
							</div>
							<div className={styles.employeesWrapper}>
								{!withoutDocuments && parentalDoc?.originalName && (
									<div
										className={clsx(
											styles.parentalBlock,
											documentState(styles, {
												pending: parentalPending,
												approved: item.reviewedParentalConfirmation,
												rejected: item.rejectedParentalConfirmation
											})
										)}
									>
										<ParentalConfirmation
											parentalConfirmationHashName={parentalDoc.name}
											originalName={parentalDoc.originalName}
										/>
									</div>
								)}
								{parentalDoc &&
									(readOnly ? (
										<ReviewState
											caption={t('Elternbestätigung')}
											pending={parentalPending}
											rejected={!!item.rejectedParentalConfirmation}
											approved={!!item.reviewedParentalConfirmation}
										/>
									) : (
										<ReviewSwitch
											caption={t('Elternbestätigung')}
											pending={parentalPending}
											locked={isLocked}
											rejected={!!item.rejectedParentalConfirmation}
											approved={!!item.reviewedParentalConfirmation}
											onReject={() =>
												onRejectParental(item.id, item.companyId, item.firstName, item.gender)
											}
											onApprove={() => onApproveParental(item.id, index)}
										/>
									))}
							</div>
						</div>
					)
				})}

				{invitationUser?.map(item => (
					<div key={item.id} className={clsx(styles.emploeeContainer, isList && styles.personRow)}>
						<div className={styles.personIdentity}>
							<span className={styles.employeeProfile}>
								<Avatar
									className={styles.userAvatar}
									round
									size={32}
									name={item.email || '?'}
									color="#7b8fa8"
									src={avatarUrls[item.avatarHashName] || ''}
								/>
								{/* Ohne Adresse (Alumni, #213) steht dort, was es ist. */}
								<p className={styles.name}>{item.email || t('Invited person')}</p>
							</span>
						</div>
						<p className={styles.status}>{t('Invitation Pending')}</p>
					</div>
				))}

				{!employees?.length && !invitationUser?.length && (
					<div className={styles.emptyPanel}>{t('No employees yet')}</div>
				)}
			</div>
		</section>
	)
}

PeopleTab.propTypes = {
	companyInfo: PropTypes.object,
	readOnly: PropTypes.bool,
	// Nur-Lese-Zugang Alumni (#213): Status der Nachweise ja, die Dateien nicht.
	withoutDocuments: PropTypes.bool,
	employees: PropTypes.array,
	invitationUser: PropTypes.array,
	avatarUrls: PropTypes.object.isRequired,
	workAreas: PropTypes.array,
	view: PropTypes.string.isRequired,
	onChangeView: PropTypes.func.isRequired,
	onRejectParental: PropTypes.func,
	onApproveParental: PropTypes.func
}

export { VIEWS }
